import React, { useEffect, useState } from 'react';
import { Card, Typography, Row, Col, Statistic, Skeleton, Space, Tag, Divider } from 'antd';
import { 
  DollarOutlined, 
  ClockCircleOutlined,
  RiseOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { dashboardService, PaymentStats } from '../../services/dashboardService';
import { useAuth } from '../../contexts/AuthContext';

const { Title, Text } = Typography;

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount) + ' FCFA';
};

const PaymentWidget: React.FC = () => {
  const { currentUser } = useAuth();
  const isAgent = currentUser?.role === 'agent';

  const [stats, setStats] = useState<PaymentStats | null>(null);
  const [loading, setLoading] = useState(!isAgent);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isAgent) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await dashboardService.getPaymentStats();
        setStats(data);
        setError(null);
      } catch (err) {
        console.error('Erreur chargement stats paiement:', err);
        setError('Impossible de charger les statistiques');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAgent]);

  if (isAgent) {
    return null;
  }

  if (loading) {
    return (
      <Card style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Skeleton active paragraph={{ rows: 4 }} />
      </Card>
    );
  }

  if (error) {
    return (
      <Card style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <div style={{ textAlign: 'center', color: '#ff4d4f' }}>
          <ExclamationCircleOutlined style={{ fontSize: 24, marginBottom: 8 }} />
          <div>{error}</div>
        </div>
      </Card>
    );
  }

  return (
    <Card 
      title={
        <Space>
          <DollarOutlined style={{ color: '#52c41a' }} />
          <span>Paiements & Revenus</span>
        </Space>
      }
      style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
    >
      {/* Revenus */}
      <Title level={5} style={{ marginBottom: 16, color: '#52c41a' }}>
        <RiseOutlined /> Revenus
      </Title>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={8}>
          <Statistic
            title="Aujourd'hui"
            value={stats?.revenue.today || 0}
            suffix="FCFA"
            valueStyle={{ color: '#3f8600', fontSize: '16px' }}
          />
        </Col>
        <Col xs={8}>
          <Statistic
            title="Cette semaine"
            value={stats?.revenue.week || 0}
            suffix="FCFA"
            valueStyle={{ color: '#1890ff', fontSize: '16px' }}
          />
        </Col>
        <Col xs={8}>
          <Statistic
            title="Ce mois"
            value={stats?.revenue.month || 0}
            suffix="FCFA"
            valueStyle={{ color: '#722ed1', fontSize: '16px' }}
          />
        </Col>
      </Row>

      <Divider style={{ margin: '16px 0' }} />

      {/* Paiements en attente */}
      <Title level={5} style={{ marginBottom: 16, color: '#fa8c16' }}>
        <ClockCircleOutlined /> Paiements en attente
      </Title>
      
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
          <Space>
            <Tag color="blue">Commandes</Tag>
            <Text type="secondary">{stats?.pending.orders.count || 0}</Text>
          </Space>
          <Text strong style={{ color: '#1890ff' }}>
            {formatCurrency(stats?.pending.orders.amount || 0)}
          </Text>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
          <Space>
            <Tag color="orange">Diagnostics</Tag>
            <Text type="secondary">{stats?.pending.diagnostics.count || 0}</Text>
          </Space>
          <Text strong style={{ color: '#fa8c16' }}>
            {formatCurrency(stats?.pending.diagnostics.amount || 0)}
          </Text>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
          <Space>
            <Tag color="purple">Abonnements</Tag>
            <Text type="secondary">{stats?.pending.subscriptions.count || 0}</Text>
          </Space>
          <Text strong style={{ color: '#722ed1' }}>
            {formatCurrency(stats?.pending.subscriptions.amount || 0)}
          </Text>
        </div>

        <Divider style={{ margin: '8px 0' }} />

        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          padding: '12px', 
          backgroundColor: '#fafafa', 
          borderRadius: '8px' 
        }}>
          <Space>
            <Tag color="red">Total en attente</Tag>
            <Text strong>{stats?.pending.total.count || 0} paiements</Text>
          </Space>
          <Text strong style={{ color: '#ff4d4f', fontSize: '16px' }}>
            {formatCurrency(stats?.pending.total.amount || 0)}
          </Text>
        </div>
      </Space>
    </Card>
  );
};

export default PaymentWidget;
